import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import Card from "@/components/base/Card";
import Badge from "@/components/base/Badge";
import Button from "@/components/base/Button";
import Input from "@/components/base/Input";
import { aiService } from "@/services/api";

export default function AdminReportesPage() {
  const [periodo, setPeriodo] = useState("semana");
  const [search, setSearch] = useState("");
  const [feedbackFilter, setFeedbackFilter] = useState("todos");

  const [consultas, setConsultas] = useState<any[]>([]);
  const [stats, setStats] = useState({ total: 0, sinResp: 0, pend: 0, rate: 100 });
  const [gaps, setGaps] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Modal states
  const [viewingLog, setViewingLog] = useState<any | null>(null);
  const [reviewingGap, setReviewingGap] = useState<any | null>(null);
  const [gapResolution, setGapResolution] = useState("");

  const fetchLogs = async () => {
    try {
      setLoading(true);
      setError("");
      const data = await aiService.listConsultas({
        periodo,
        feedback: feedbackFilter,
        search,
      });
      setConsultas(data.consultas);
      setStats(data.stats);
      setGaps(data.gaps);
    } catch (err: any) {
      console.error("Error loading audit logs:", err);
      setError("Error al cargar logs de auditoría: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [periodo, feedbackFilter, search]);

  const handleResolveGap = async (gapId: string) => {
    try {
      setLoading(true);
      await aiService.resolverBrecha(gapId, gapResolution);
      setReviewingGap(null);
      setGapResolution("");
      alert("Vacío normativo marcado como revisado y resuelto.");
      await fetchLogs();
    } catch (err: any) {
      console.error("Error resolving gap:", err);
      alert("Error al resolver vacío normativo: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto space-y-6 p-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-foreground-900 tracking-tight">Auditoría del Sistema</h1>
          <p className="text-sm text-foreground-500 mt-1">Supervisión y control de consultas al asistente, trazabilidad de logs y análisis de brechas del reglamento (RICE).</p>
        </div>
        {loading && (
          <i className="ri-loader-4-line animate-spin text-xl text-primary-500" />
        )}
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Period selector */}
      <div className="flex items-center gap-2">
        <span className="text-sm font-semibold text-foreground-500">Período:</span>
        {["hoy", "semana", "mes", "todos"].map((p) => (
          <button
            key={p}
            onClick={() => setPeriodo(p)}
            className={`text-xs px-3 py-1.5 rounded-full transition-all duration-200 cursor-pointer capitalize ${
              periodo === p
                ? "bg-primary-500 text-white font-medium shadow-sm"
                : "bg-background-100 text-foreground-600 hover:bg-background-200"
            }`}
          >
            {p === "todos" ? "Todo el historial" : p}
          </button>
        ))}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-foreground-500">Consultas en Período</span>
            <i className="ri-chat-3-line text-primary-500" />
          </div>
          <p className="text-2xl font-bold text-foreground-900">{stats.total}</p>
          <p className="text-xs text-foreground-400 mt-1">Total interacciones registradas</p>
        </Card>
        <Card>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-foreground-500">Sin Respuesta / Brechas</span>
            <i className="ri-question-line text-secondary-500" />
          </div>
          <p className="text-2xl font-bold text-secondary-600">{stats.sinResp}</p>
          <p className="text-xs text-foreground-400 mt-1">Consultas catalogadas inútiles</p>
        </Card>
        <Card>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-foreground-500">Pendientes Revisión</span>
            <i className="ri-flag-line text-red-500" />
          </div>
          <p className="text-2xl font-bold text-red-600">{stats.pend}</p>
          <p className="text-xs text-foreground-400 mt-1">Sin feedback del usuario</p>
        </Card>
        <Card>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-foreground-500">Tasa de Utilidad</span>
            <i className="ri-thumb-up-line text-accent-500" />
          </div>
          <p className="text-2xl font-bold text-accent-600">{stats.rate}%</p>
          <p className="text-xs text-foreground-400 mt-1">Respuestas marcadas útiles</p>
        </Card>
      </div>

      {/* Gap analysis */}
      {gaps.length > 0 && (
        <Card>
          <h3 className="text-base font-semibold text-foreground-900 mb-4">
            <i className="ri-alert-line text-secondary-500 mr-2" />
            Vacíos normativos detectados ({gaps.length})
          </h3>
          <p className="text-sm text-foreground-500 mb-4">
            Situaciones consultadas para las cuales no se encontró norma específica en los documentos cargados.
            Se recomienda revisar y actualizar el RICE para cubrir estas situaciones.
          </p>
          <div className="space-y-3">
            {gaps.map((item) => (
              <div key={item.id} className="flex items-center justify-between gap-3 p-3 rounded-lg bg-secondary-50 border border-secondary-200/50">
                <div className="flex items-start gap-2.5">
                  <i className="ri-error-warning-line text-secondary-500 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-foreground-800">{item.text}</p>
                    <p className="text-xs text-foreground-500 mt-0.5">
                      Consultada {item.count} veces · Última: {item.lastDate}
                    </p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => setReviewingGap(item)} className="cursor-pointer">
                  Revisar
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Logs Table Area */}
      <div className="space-y-3">
        {/* Controls */}
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center justify-between">
          <Input
            placeholder="Filtrar por usuario, consulta o rol..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            icon={<i className="ri-search-line" />}
            className="max-w-sm"
            fullWidth={false}
          />
          <div className="flex gap-2 overflow-x-auto">
            {[
              { key: "todos", label: "Todos" },
              { key: "util", label: "Útiles" },
              { key: "revisar", label: "Brechas / Revisar" },
              { key: "pendiente", label: "Pendientes" },
            ].map((f) => (
              <button
                key={f.key}
                onClick={() => setFeedbackFilter(f.key)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border cursor-pointer capitalize transition-all ${
                  feedbackFilter === f.key
                    ? "bg-foreground-900 text-white border-foreground-900"
                    : "bg-white text-foreground-600 border-background-200 hover:bg-background-50"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <Card padding="none">
          <div className="px-4 py-3 border-b border-background-200">
            <h3 className="text-base font-semibold text-foreground-900">Logs de Consultas Recientes</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-background-200 bg-background-50/50">
                  <th className="text-left py-2.5 px-4 text-foreground-500 font-medium text-xs">Fecha</th>
                  <th className="text-left py-2.5 px-4 text-foreground-500 font-medium text-xs">Usuario</th>
                  <th className="text-left py-2.5 px-4 text-foreground-500 font-medium text-xs">Módulo</th>
                  <th className="text-left py-2.5 px-4 text-foreground-500 font-medium text-xs">Consulta</th>
                  <th className="text-center py-2.5 px-4 text-foreground-500 font-medium text-xs">Feedback</th>
                  <th className="text-right py-2.5 px-4 text-foreground-500 font-medium text-xs">Acción</th>
                </tr>
              </thead>
              <tbody>
                {consultas.map((log) => (
                  <tr key={log.id} className="border-b border-background-100 hover:bg-background-50/50 transition-colors">
                    <td className="py-2.5 px-4 text-xs text-foreground-500 whitespace-nowrap">{log.fecha}</td>
                    <td className="py-2.5 px-4 text-foreground-850 font-semibold text-xs whitespace-nowrap">
                      {log.user} <span className="text-[10px] text-foreground-400 font-normal">({log.rol})</span>
                    </td>
                    <td className="py-2.5 px-4">
                      <Badge variant="neutral" size="sm">{log.modulo.replace("_", " ")}</Badge>
                    </td>
                    <td className="py-2.5 px-4 text-foreground-600 max-w-[220px] truncate text-xs">{log.pregunta}</td>
                    <td className="py-2.5 px-4 text-center">
                      {log.util === true && <Badge variant="primary" size="sm">Útil</Badge>}
                      {log.util === false && <Badge variant="danger" size="sm">Revisar</Badge>}
                      {log.util === null && <Badge variant="neutral" size="sm">Pendiente</Badge>}
                    </td>
                    <td className="py-2.5 px-4 text-right">
                      <Button variant="ghost" size="sm" onClick={() => setViewingLog(log)} className="cursor-pointer">
                        Ver
                      </Button>
                    </td>
                  </tr>
                ))}
                {consultas.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-12 text-center text-foreground-400">
                      No se encontraron registros de consultas.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* ── MODAL: VER DETALLE LOG ── */}
      {viewingLog && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setViewingLog(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg p-6 space-y-4 shadow-xl animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center pb-2 border-b border-background-150">
              <h2 className="font-bold text-foreground-950 text-base">Detalle de Consulta IA</h2>
              <button type="button" onClick={() => setViewingLog(null)} className="cursor-pointer text-foreground-400 hover:text-foreground-700">
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="space-y-3 text-xs leading-relaxed">
              <div>
                <span className="font-semibold text-foreground-500">Fecha y Hora:</span>
                <p className="text-foreground-800">{viewingLog.fecha}</p>
              </div>
              <div>
                <span className="font-semibold text-foreground-500">Usuario Responsable:</span>
                <p className="text-foreground-800">{viewingLog.user} ({viewingLog.rol})</p>
              </div>
              <div>
                <span className="font-semibold text-foreground-500">Consulta:</span>
                <p className="text-foreground-900 font-medium bg-background-50 p-2.5 rounded-lg border border-background-100">{viewingLog.pregunta}</p>
              </div>
              <div>
                <span className="font-semibold text-foreground-500">Feedback Calificación:</span>
                <p className="mt-1">
                  {viewingLog.util === true && <Badge variant="primary">ÚTIL</Badge>}
                  {viewingLog.util === false && <Badge variant="danger">REQUIERE REVISIÓN (BRECHA)</Badge>}
                  {viewingLog.util === null && <Badge variant="neutral">SIN FEEDBACK</Badge>}
                </p>
              </div>
            </div>
            <div className="pt-2">
              <Button type="button" onClick={() => setViewingLog(null)} className="w-full bg-primary-500 text-white cursor-pointer">
                Cerrar
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── MODAL: REVISAR VACÍO NORMATIVO ── */}
      {reviewingGap && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => setReviewingGap(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6 space-y-4 shadow-xl animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center pb-2 border-b border-background-150">
              <h2 className="font-bold text-foreground-950 text-base">Revisar Brecha Normativa</h2>
              <button type="button" onClick={() => setReviewingGap(null)} className="cursor-pointer text-foreground-400 hover:text-foreground-700">
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="space-y-3">
              <div className="bg-amber-50 border border-amber-200 text-amber-900 text-xs p-3 rounded-lg">
                <strong>Situación no normada:</strong>
                <p className="mt-1">{reviewingGap.text}</p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Acción Correctiva / Enmienda RICE</label>
                <textarea
                  value={gapResolution}
                  onChange={(e) => setGapResolution(e.target.value)}
                  placeholder="Redacte la propuesta de artículo o directriz formativa que resolverá esta brecha..."
                  rows={4}
                  className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary-300 resize-none text-foreground-800"
                />
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={() => handleResolveGap(reviewingGap.id)} className="flex-1 bg-primary-500 text-white cursor-pointer" disabled={!gapResolution.trim()}>
                Resolver Brecha
              </Button>
              <Button type="button" variant="ghost" onClick={() => setReviewingGap(null)} className="flex-1 cursor-pointer">
                Cancelar
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}